// studioWork — the assistant's running turns, as working cards on the Studio
// canvas (pen B-04). Pure: a projection of the chat sessions the shell already
// holds (the same `drafting` + live activity the chat's step line reads), never
// domain state. A card exists while its turn runs; when the turn ends the card
// is gone and what the turn made shows up as real cards (the Library's live
// refresh). A turn that ended in a failure leaves an interrupted card; the view
// fades it out.
//
// Where a card sits: next to the cards the turn's message referenced (the
// selection "Make a module" sent), else nowhere in particular (`anchor: null`;
// the view puts it in the middle of the viewport).

export type { StudioWork, TurnSession };
export { projectStudioWork };

import type { AuthorEntry, ChatEntry, LiveActivity } from "../Chat/types.js";
import type { Placement, Point } from "./studioLayout.js";

// What a chat session says about its turn — the chat's ChatSession fits it.
type TurnSession<F> = {
    readonly drafting: boolean;
    readonly liveActivity: LiveActivity | null;
    readonly turnError: F | null;
    readonly conversation: readonly ChatEntry[];
};

type StudioWork =
    | {
          readonly type: "working";
          readonly conversationId: string;
          // What it is doing now; planning until the turn stream has said
          // (the chat's own step line reads the same).
          readonly activity: LiveActivity;
          // When the turn's message was recorded (epoch ms), if known.
          readonly startedAt?: number;
          readonly anchor: Point | null;
      }
    | {
          readonly type: "interrupted";
          readonly conversationId: string;
          readonly reason: string;
          readonly anchor: Point | null;
      };

// Room between the referenced cards and the working card; the working card's
// footprint, and how far a blocked spot moves down to look for room.
const GAP = 32;
const WORK_WIDTH = 280;
const WORK_HEIGHT = 96;
const STEP = 16;
const MAX_STEPS = 200;

type Box = {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
};
const overlaps = (a: Box, b: Box): boolean =>
    a.x < b.x + b.w + GAP / 2 &&
    b.x < a.x + a.w + GAP / 2 &&
    a.y < b.y + b.h + GAP / 2 &&
    b.y < a.y + a.h + GAP / 2;

// The first spot at or below `at` where a working card covers nothing.
const freeSpot = (at: Point, taken: readonly Box[]): Point => {
    const box = (y: number): Box => ({
        x: at.x,
        y,
        w: WORK_WIDTH,
        h: WORK_HEIGHT,
    });
    const step = [...Array(MAX_STEPS).keys()].find(
        (i) => !taken.some((t) => overlaps(box(at.y + i * STEP), t)),
    );
    return { x: at.x, y: at.y + (step ?? 0) * STEP };
};

// The turn's message: the conversation's last author entry.
const lastAuthor = (
    conversation: readonly ChatEntry[],
): AuthorEntry | undefined =>
    conversation
        .flatMap((e): readonly AuthorEntry[] =>
            e.type === "author" ? [e] : [],
        )
        .at(-1);

// The ids the turn's message referenced (the selection it was sent with).
const referencedBy = (
    conversation: readonly ChatEntry[],
): readonly string[] => {
    const last = lastAuthor(conversation);
    return last === undefined
        ? []
        : [
              ...(last.mentions ?? []).map((m) => m.id),
              ...(last.referencedIds ?? []),
          ];
};

const startedAtOf = (conversation: readonly ChatEntry[]): number | undefined =>
    lastAuthor(conversation)?.timestamp;

// To the right of the referenced cards, level with the topmost.
const anchorBeside = (
    ids: readonly string[],
    placements: ReadonlyMap<string, Placement>,
): Point | null => {
    const boxes = ids.flatMap((id) => {
        const p = placements.get(id);
        return p === undefined ? [] : [p];
    });
    if (boxes.length === 0) return null;
    return {
        x: Math.max(...boxes.map((b) => b.x + b.width)) + GAP,
        y: Math.min(...boxes.map((b) => b.y)),
    };
};

const projectStudioWork = <F>(
    sessions: ReadonlyMap<string, TurnSession<F>>,
    placements: ReadonlyMap<string, Placement>,
    failureText: (failure: F) => string,
): readonly StudioWork[] => {
    const work = [...sessions].flatMap(
        ([conversationId, s]): readonly StudioWork[] => {
            const anchor = anchorBeside(
                referencedBy(s.conversation),
                placements,
            );
            if (s.drafting) {
                const startedAt = startedAtOf(s.conversation);
                return [
                    {
                        type: "working",
                        conversationId,
                        activity: s.liveActivity ?? { type: "thinking" },
                        ...(startedAt === undefined ? {} : { startedAt }),
                        anchor,
                    },
                ];
            }
            return s.turnError === null
                ? []
                : [
                      {
                          type: "interrupted",
                          conversationId,
                          reason: failureText(s.turnError),
                          anchor,
                      },
                  ];
        },
    );
    // Each anchored card takes the first free spot by its anchor, clear of
    // the cards and of the working cards placed before it.
    const cardBoxes: readonly Box[] = [...placements.values()].map((p) => ({
        x: p.x,
        y: p.y,
        w: p.width,
        h: p.height,
    }));
    return work.reduce<{
        readonly taken: readonly Box[];
        readonly out: readonly StudioWork[];
    }>(
        (acc, w) => {
            if (w.anchor === null) return { ...acc, out: [...acc.out, w] };
            const at = freeSpot(w.anchor, acc.taken);
            return {
                taken: [
                    ...acc.taken,
                    { x: at.x, y: at.y, w: WORK_WIDTH, h: WORK_HEIGHT },
                ],
                out: [...acc.out, { ...w, anchor: at }],
            };
        },
        { taken: cardBoxes, out: [] },
    ).out;
};
